/**
 * **打印域的单位换算** —— 这是整个包里唯一允许做 mm ↔ 设备点换算的地方。
 *
 * ## 为什么这几行值得单独一个文件
 *
 * `print-template-architecture.md` §4.4 把话说死了：
 *
 * > 文档层用 mm，IR 层用设备点，**只允许一个换算函数**。
 *
 * 因为「预览和打印差一点点」这类问题**不报错**、只在真机上看得见
 * （0.1mm 的线在 203dpi 下只有 0.8 个点，四舍五入往哪边倒就是两种结果）。
 * 换算一旦散落在几处，对不上的时候没人查得出是哪一处偏了。
 *
 * ⚠️ 这些是**打印域**的知识（纸张 / DPI / 静区），所以它在包里而不在内核里 ——
 * 判据见 `package-boundaries.md` §10：「换个域还要不要它」。
 * UI 设计域里没有 dpi 这个概念。
 *
 * ⚠️ 也**不要**把 `mmToDots` 做成「四舍五入到 0.01mm 再算」的两段式：
 * 中间那次舍入会在高 dpi 下多丢一个点（203dpi 的 0.01mm 只有 0.08 个点，
 * 但它会把 799.2 变成 799.0 再算，结果一样而误差来源说不清了）。
 * 一次算完。
 */

/** 英寸 → 毫米（国际英寸定义值，精确） */
export const MM_PER_INCH = 25.4

/** 常见的热敏 / 激光分辨率。档位是**数据**，界面上直接列它 */
export const DPI_PRESETS: readonly number[] = [203, 300, 600]

/** 默认分辨率：203dpi（1 点 = 0.125mm）是标签机最常见的档 */
export const DEFAULT_DPI = 203

/**
 * mm → **设备点**（整数）。
 *
 * ⚠️ 取整方式必须和输出侧一致（`Math.round`，不是 `floor`）：条码的
 * X 维度按 `floor` 得恒定整数模块宽（BWIPP 的做法），而**几何位置**按
 * `round` —— 两者混用会让「条码整体右移半个点」，扫描等级掉一档。
 */
export function mmToDots(mm: number, dpi: number): number {
  return Math.round((mm * dpi) / MM_PER_INCH)
}

/** 设备点 → mm（只用于显示与对拍，不参与排版决策） */
export function dotsToMm(dots: number, dpi: number): number {
  return (dots * MM_PER_INCH) / dpi
}

/**
 * **条码 X 维度**：微米 → 整数点。
 *
 * 为什么 X 维度用微米存（`xDimMicron`）：GS1 的标称值是 0.264mm 这种
 * 三位小数的毫米数，浮点存会在模板 JSON 里漂移；微米是整数。
 *
 * ⚠️ `max(1, …)`：算出 0 的模块宽会让整条码变成一条实心黑边，
 * 而那是**看起来像条码、扫不出来**的最坏形态。宁可 1 个点（会报窄）。
 */
export function micronToDots(micron: number, dpi: number): number {
  return Math.max(1, Math.round(((micron / 1000) * dpi) / MM_PER_INCH))
}

/**
 * GS1 允许的最小 X 维度（**毫米**）—— 0.264mm。
 *
 * 来源：GS1 GSCN-23-041（见 `print-template-architecture.md` §5.1）。
 * 203dpi 下 0.264mm 需要 **3 个点**（2 点 = 0.25mm 已低于下限）——
 * 这就是「1 个点也是合法值」和「能不能扫出来」之间的差别。
 */
export const GS1_MIN_X_MM = 0.264

/** 满足 GS1 下限所需的最少点数（203dpi → 3） */
export function minGs1ModuleDots(dpi: number): number {
  return Math.ceil((GS1_MIN_X_MM * dpi) / MM_PER_INCH)
}

/**
 * mm 的**文档层精度**：0.01mm。
 *
 * ⚠️ 只在**文档层**（用户输入 / 显示 / 存盘）用，不要拿它去喂 `mmToDots` ——
 * 见文件头的「不要两段式」。
 */
export function roundMm(mm: number): number {
  return Math.round(mm * 100) / 100
}

/** 把值夹在区间里（拖拽 / 输入都要过这一道） */
export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max)
}

/**
 * 把任意角度收成 **0 / 90 / 180 / 270**。
 *
 * ⚠️ 只支持四个直角，是**刻意的**：
 *   · 标签上的旋转几乎只用这四档（竖排文字、竖排条码）；
 *   · 任意角度会让命中判定、吸附、缩放的数学全变成一般情况
 *     （还要处理"旋转之后的包围盒跟纸不平行"）；
 *   · 收成四档之后，命中判定就是"把点反向转 90° 的整数倍" ——
 *     精确、无三角函数误差，也不需要矩阵求逆。
 *
 * 放在 `units.ts` 而不是 `pageModel.ts`：**求值层也要用它**
 * （旋转是"这个组件怎么画"的一部分，要进组件值），
 * 而求值层不该依赖视图模型（那是渲染侧的东西）。
 */
export function normalizeRotation(raw: unknown): number {
  const value = typeof raw === 'number' ? raw : Number(raw)
  if (!Number.isFinite(value)) return 0
  return ((Math.round(value / 90) * 90) % 360 + 360) % 360
}

/**
 * **默认纸张**（60×30mm @203dpi，边距 2mm）—— 常见热敏标签的规格。
 *
 * ⚠️ 只许有一处默认值：文档节点没写、旧文档里页面又没写时，读的就是它。
 * 散在几处的话会出现"编辑器 60×30、IR 里 80×60"这种对不上的情况。
 */
export const DEFAULT_PAPER = {
  /*
   * ⚠️ 默认是**常见热敏标签**的尺寸（60×30mm，三防纸那种一卷一卷的）——
   * 真机反馈："关于文档的默认大小使用 60×30 的尺寸大小"。
   * 原来那个 60×30 是 A4 族的比例，对热敏机不是常见规格。
   */
  widthMm: 60,
  heightMm: 30,
  dpi: DEFAULT_DPI,
  marginMm: 2,
} as const

/**
 * **默认纸色：白**。
 *
 * ⚠️ 它不是一个"随便挑的好看颜色"，而是一条**输出语义**：
 * 白 = 不铺底（热敏纸本来的颜色），别的颜色 = 整张铺满。
 * 所以默认值只许有这一处（IR 里也要带着它，见 `type-contract` §8.1）。
 */
export const DEFAULT_BACKGROUND = '#ffffff'
